import { Address, SelectionDict } from '@/models/generics'
import {
  CtOrder,
  CtCatalogue,
  CtCatalogueProduct,
  CtInvoice,
  CtStockItem,
  CtStockLedger,
  CtInventoryLocation,
  CtStorageLocation,
  CtImport,
  CtProduct,
  CtPacking,
  CtSource,
  CtClient,
  CtCategory,
  CtSourceOrder,
  CtTask,
  CtStorageType,
  PackingItem
} from '@/models/interfaces/content-type'
import { InsightResolution, InsightPoint } from '@/models/interfaces/insight'

import { Status } from '@/models/status'
import { InvoiceDefinition, InvoiceGenerationDef, InvoiceHeader } from '@/pages/Invoice/interfaces'
import { ServiceEventHandlers } from '@/store/modules/services'
import { AxiosInterceptors, RequestHandler, ServiceResponse, User } from '@Protium/vue-ozone-utils'
import { UUID } from '@Protium/vue-ozone-utils/types/utils'
import { Dispatch, DispatchOptions } from 'vuex'
import _ from 'lodash'

import IO from './io'
import { $settle } from '@/utils/promise'
import SKU from '@/utils/sku'
import { $castArray } from '@/utils'

export type Enumerate<N extends number, Acc extends number[] = []> = Acc['length'] extends N
  ? Acc[number]
  : Enumerate<N, [...Acc, Acc['length']]>

export type Range<F extends number, T extends number> = Exclude<Enumerate<T>, Enumerate<F>>

export interface StatusPattern {
  id: Status,
  index?: number,
  additive?: boolean,
  immutable?: boolean,
  from?: Array<Status> | boolean,
  to?: Array<Status> | boolean,
  weight?: Range<0, 11>
}

export type StatusPatternObj = {
  options (current: Array<Status>): Array<StatusPattern>,
  parse (from: Array<Status>, to: Array<Status>, clean?: boolean): Array<Status>,
  compare (method: 'gt' | 'gte' | 'lt' | 'lte', lhs: Array<Status>, rhs: Status): boolean
} & {
  [key: string]: StatusPattern
}

const DISPATCH_DEFAULT = (type: string, payload?: any, options?: any): Promise<any> => { return Promise.reject('No dispatch implemented. pass it from the vuex module that generated this service') }

export enum ContentType {
  Task = 'task',
  Prop = 'prop',
  Category = 'category',
  CatalogueProduct = 'catalogue_product',
  CatalogueStockItem = 'catalogue_stock_item',
  Catalogue = 'catalogue',
  SupplierCatalogue = 'supplier_catalogue',
  Client = 'client',
  Supplier = 'supplier',
  Datamap = 'datamap',
  Import = 'import',
  InventoryLocation = 'inventory_location',
  Invoice = 'invoice',
  Order = 'order',
  SourceOrder = 'source_order',
  Packing = 'packing',
  Product = 'product',
  Source = 'source',
  StockItem = 'stock',
  StockLedger = 'stock_ledger',
  StorageLocation = 'storage_location',
  StorageType = 'storage_type',
  Asset = 'asset',
  Event = 'event',
  ShipmentCarrier = 'shipment_carrier'
}

export interface Timerange<F = Date | string> {
  start: F;
  end: F;
}

export enum DirectrEvent {
  'Create/Import' = 'create/import',
  'Update/Import' = 'update/import',
  'Create/Supplier' = 'create/supplier',
  'Update/Supplier' = 'update/supplier',
  'Create/Supplier_catalogue' = 'create/supplier_catalogue',
  'Update/Supplier_catalogue' = 'update/supplier_catalogue',
  'Create/Client' = 'create/client',
  'Update/Client' = 'update/client',
  'Create/Catalogue' = 'create/catalogue',
  'Update/Catalogue' = 'update/catalogue',
  'Create/CatalogueProduct' = 'create/catalogue_product',
  'Update/CatalogueProduct' = 'update/catalogue_product',
  'Create/Product' = 'create/product',
  'Update/Product' = 'update/product',
  'Create/Stock' = 'create/stock',
  'Update/Stock' = 'update/stock',
  'Create/Order' = 'create/order',
  'Update/Order' = 'update/order',
  'Create/SourceOrder' = 'create/source_order',
  'Update/SourceOrder' = 'update/source_order',
  'Warn/Invoice' = 'warn/invoice',
  'Generate/Invoice' = 'generate/invoice',
  'Create/Invoice' = 'create/invoice',
  'Create/InvoiceDraft' = 'create/draft:invoice',
  'Update/Invoice' = 'update/invoice',
  'Create/StockLedger' = 'create/stock_ledger',
  'Update/StockLedger' = 'update/stock_ledger',
  'Create/InventoryLocation' = 'create/inventory_location',
  'Update/InventoryLocation' = 'update/inventory_location',
  'Create/StorageLocation' = 'create/storage_location',
  'Update/StorageLocation' = 'update/storage_location',
  'Error' = 'error',
  'Info' = 'info',
  Ping = 'ping',
  Scan = 'scan'
}

enum ServiceDispatchType {
  Error = 'Error'
}

interface ServiceDispatch extends Dispatch {
  (type: ServiceDispatchType, payload?: any, options?: DispatchOptions): Promise<any>
}

interface ServiceProps {
  interceptors: AxiosInterceptors,
  getters: any,
  dispatch: ServiceDispatch,
  handlers: ServiceEventHandlers
}

const timerangeQuery = (timerange, resolution) => [{ field: 'start', match: timerange.start }, { field: 'end', match: timerange.end }, { field: 'resolution', match: resolution }]

export default class Directr extends RequestHandler<any> {
  private io: IO | undefined
  private getters: any
  private dispatch: ServiceDispatch | ((type, callback?, options?) => Promise<any>)
  private handlers: ServiceEventHandlers
  private _user?: User

  constructor(config?: Record<string, any>, props?: ServiceProps) {
    super(
      {
        service: config?.service || { id: 'directr', name: 'Directr' },
        ...(config || {}),
        // debug: process.env.NODE_ENV === 'production' ? false : (config?.debug || false),
        debug: false,
        requestConfig: {
          // baseURL: '',
          withCredentials: true,
          ...((config || {}).requestConfig || {}),
          headers: {
            ...(((config || {}).requestConfig || {}).headers || {}),
            'x-ozone-app-type': process.env.OZONE_SERVICE_TYPE_ID,
            Authorization: process.env.API_AUTH_TOKEN,
            'x-service-config': '{"limit": 1000}'
          }
        }
      },
      props
    )

    this._user = config?.user

    this.getters = props?.getters
    this.dispatch = props?.dispatch || DISPATCH_DEFAULT
    this.handlers = props?.handlers || { Error: e => Promise.reject(e), Warn: e => Promise.reject(e), Fatal: e => Promise.reject(e), Unauthorized: e => Promise.reject(e) }

    if (((config || {}).service || {}).io) {
      this.io = new IO(config?.requestConfig.baseURL, {}, this._user?.id as UUID)
      this.io.init('/directr')
    }
  }

  async aggregate (aggregationType: string, timerange: Timerange) {
    return await this.get(`aggregate.${aggregationType}`, timerange).catch(e => this.handlers.Error(e))
  }

  get user () {
    return this._user?.id
  }

  get $io () {
    return this.io
  }

  get listeners () {
    return this.io?.listeners
  }

  listen (event: DirectrEvent | `${string}/${string}` | `${string}/${string}/${string}`, callback: (payload: any) => any) {
    if (!this.io) return console.error('[Error] Directr.listen | Cannot set listener, io is not initialized')
    // if (this.io.listeners[event]) console.warn('[Directr] overwriting listener for event', event)
    this.io.listen(event, callback)
  }

  speak (type: DirectrEvent | `${string}/${string}` | `${string}/${string}/${string}`, message) {
    this.io?.speak(type, message)
  }

  // @CtOrder
  get order () {
    return {
      update: async (id: UUID, data: Partial<CtOrder>) => {
        // TODO verify data integrity
        // console.log('update order', data, data.tags)
        return await this.patch('id', id, data).catch(e => this.handlers.Error(e))
      },
      // NOTE: strategy, exclude & timerange options are not implemented yet
      pickable: async (storage_location: UUID, options?: { createTask?: boolean, limit?: number, catalogues?: Array<UUID>, newAsPickable?: boolean, strategy?: string, exclude?: Array<UUID>, timerange?: Timerange }) => {
        return this.get('orders.pickable', [storage_location], { resolve: options?.createTask, limit: options?.limit, force: options?.newAsPickable, query: { always: options?.catalogues } }).then(data => data.items?.[0])
      },
      pick: async (id: UUID | Array<UUID>, selection?: Array<string>, taskId?: string) => {
        return await this.put('order.pick', id, selection, { resolve: taskId }).catch(e => this.handlers.Error(e))
      },
      fulfill: async (id: UUID, itemSelection?: Array<string>) => {
        const selection = $castArray(itemSelection).filter(s => typeof s === 'string')
        return await this.put('order.fulfill', id, selection.length ? selection : undefined).catch(e => this.handlers.Error(e))
      },
      cancel: async (id: UUID, selection: Array<string>) => {
        return await this.put('order.replace', id, selection).catch(e => this.handlers.Error(e))
      },
      replace: async (id: UUID, data: Record<string, any>, config?) => {
        // console.log('order.replace', data)
        return await this.put('order.replace', id, data, config).catch(e => this.handlers.Error(e))
      },
      reset: async (id: UUID, products: Array<string> = []) => {
        return await this.patch('order.reset', id, products).catch(e => this.handlers.Error(e))
      },
      delete: async (id: UUID) => {
        return await this.delete('order', [{ field: 'sys.id', match: id }]).catch(e => this.handlers.Error(e))
      },
      sourcing: async (id: UUID) => {
        return await this.put('order.source', id).catch(e => this.handlers.Error(e))
      },
      sourced: async (id: UUID) => {
        return await this.put('order.sourced', id).catch(e => this.handlers.Error(e))
      },
      slip: async (id, items) => {
        return await this.get('packing-slip', [{ field: 'sys.id', method: 'is', match: id }, { field: 'fulfillments', method: 'in', match: items }]).catch(e => this.handlers.Error(e))
      },
      labels: async (id, fulfillment) => {
        return await this.get('shipment-labels', [{ field: 'sys.id', match: id }], { fulfillment }).catch(e => this.handlers.Error(e))
      },
      setStatus: async (id, status: Array<string> | { status: Array<string> }) => {
        return await this.patch('order.status', id, (status as any)?.status || status).catch(e => this.handlers.Error(e))
      },
      patchAddress: async (id, address: Partial<Address>) => {
        return await this.patch('order.address', id, address).catch(e => this.handlers.Error(e))
      },
      patchTags: async (id, tags: Array<string>) => {
        return await this.patch('order.tags', id, tags).catch(e => this.handlers.Error(e))
      },
      patchOperationalNote: async (id, note: string) => {
        return await this.patch('order.operational_note', id, { operational_notes: note }).catch(e => this.handlers.Error(e))
      },
      patchPacking: async (id, packing: Array<PackingItem>) => {
        return await this.patch('order.packing', id, packing).catch(e => this.handlers.Error(e))
      },
      fromShipment: async (shipmentId: string) => {
        // NOTE can match shipment-id or bag-id, returning all orders for a bag-id
        return await this.get('order.from:shipment', [{ match: shipmentId }])
      },
      issue: {
        resolve: async (id: UUID, payload?: { status?: Array<Status> }) => {
          return this.put('order.issue.resolve', id, payload, {})
        },
        create: async (id: UUID, reason: string, status?: Array<Status>) => {
          return this.put('order.issue', id, { type: Status.Failed, payload: reason, status })
        }
      },
      invoices: {
        pending: async (timerange: Timerange, catalogue: UUID, countOnly?: boolean) => {
          return this.get('order.invoicable', [
            { field: 'sys.created_at', method: 'gte', match: timerange.start },
            { field: 'sys.created_at', method: 'lte', match: timerange.end },
            { field: 'catalogue', method: 'is', match: catalogue }
          ], { limit: countOnly ? 1 : undefined })
        },
        processed: async (timerange: Timerange, catalogue: UUID) => {
          return this.get('order.invoiced', [{ field: 'sys.created_at', method: 'gte', match: timerange.start }, { field: 'sys.created_at', method: 'lte', match: timerange.end }, { field: 'catalogue', method: 'is', match: catalogue }])
        },
        list: async (timerange: Timerange, catalogue: UUID, limit?: number) => {
          return this.get('catalogue.invoice', [{ field: 'sys.created_at', method: 'gte', match: timerange.start }, { field: 'sys.created_at', method: 'lte', match: timerange.end }, { field: 'catalogue', method: 'is', match: catalogue }], { limit })
        }
      },
      timeregistration: {
        set: async (payload: Array<{ id: UUID, status?: boolean, data: { timeregister_picking_start?: number | null, timeregister_picking_end?: number | null, timeregister_packing_start?: number | null, timeregister_packing_end?: number | null } }>) => {
          return this.patch('order.timeregistration.set', payload.map(p => p.id), payload)
        },
        reset: async (orderId: UUID, key: 'picking-start' | 'picking-end' | 'packing-start' | 'packing-end') => {
          switch (key) {
            case 'picking-start':
              return this.order.timeregistration.set([{ id: orderId, status: true, data: { timeregister_picking_start: null } }])
            case 'picking-end':
              return this.order.timeregistration.set([{ id: orderId, data: { timeregister_picking_end: null } }])
            case 'packing-start':
              return this.order.timeregistration.set([{ id: orderId, status: true, data: { timeregister_packing_start: null } }])
            case 'packing-end':
              return this.order.timeregistration.set([{ id: orderId, data: { timeregister_packing_end: null } }])
            default:
              return this.order.timeregistration.set([{ id: orderId, data: { timeregister_picking_start: null, timeregister_picking_end: null, timeregister_packing_start: null, timeregister_packing_end: null } }])
          }
        },
        picking: {
          start: async (orderIds: Array<UUID>) => this.order.timeregistration.set(orderIds.map(id => ({ id, status: true, data: { timeregister_picking_start: Date.now() } }))),
          end: async (orderIds: Array<UUID>) => this.order.timeregistration.set(orderIds.map(id => ({ id, status: true, data: { timeregister_picking_end: Date.now() } })))
        },
        packing: {
          start: async (orderId: UUID) => this.order.timeregistration.set([{ id: orderId, status: true, data: { timeregister_packing_start: Date.now() } }]),
          end: async (orderId: UUID) => this.order.timeregistration.set([{ id: orderId, status: true, data: { timeregister_packing_end: Date.now() } }])
        }
      }
    }
  }

  get catalogue () {
    return {
      // fetches products from catalogue_product for the given catalogue
      products: async (catalogueId) => {
        return await this.get('catalogue_product', [{ field: 'catalogue', method: 'is', match: catalogueId }])
      },
      // gets all skus from catalogue_product for the given catalogue.
      // this is separated as it is meant to provide a foolproof way of always knwoing the last sku
      // regardless if the .products function somehow does not return all products, or has any other issue for whatever reason
      // items are returned as {sys: {id}, sku}
      skus: async (catalogueId) => {
        return await this.get('catalogue.skus', catalogueId)
      }

    }
  }

  get product () {
    return {
      search: async (items: Array<{ sku: string, ean: string }>, catalogue?: UUID) => {
        const results = await this.get('product.find', [
          { field: 'sku', match: items.map(i => i.sku).filter(Boolean) },
          { field: 'ean', match: items.map(i => i.ean).filter(Boolean) },
          { field: 'catalogue', match: catalogue }
        ])

        return results
      },
      searchProps: async (query: string, categories?: Array<string>, brands?: Array<string>) => {
        // if (!query) throw Error('Missing query')

        let products: Array<CtProduct> = []
        let sys
        if (query) {
          const { items, sys: s } = await this.get('product', [
            { select: ['title', 'sku', 'ean', 'images', 'components'] },
            { field: 'title', method: 'like', match: query },
            { field: 'sku', method: 'like', match: new SKU(query).value }
          ], { query: { any: true } })
          if (items?.length) products = items
          sys = s
        }

        try {
          let sQuery = !query?.length ? [] : query.split(',').flatMap(q => [{ field: 'sku', method: 'is', match: new SKU(q).value }, { field: 'ean', method: 'in', match: q as any }])
          if (categories?.length) sQuery.push({ field: 'categories', method: 'in', match: categories, type: 'uuid' } as any)
          if (brands?.length) {
            sQuery = sQuery.concat(brands.flatMap(b => ([{ field: 'brand', method: 'is', match: b }, { field: 'producer', method: 'is', match: b }])))
          }
          if (sQuery.length) {
            const { items: stock } = await this.get('stock', sQuery, { query: { any: true } })
            if (stock?.length) {
              const { items: linkedProducts } = await this.get('product', [{ select: ['title', 'sku', 'ean', 'images', 'components'] }, ...stock.map(s => ({ field: 'components', method: 'in', match: s.sys.id, type: 'uuid' }))], { query: { any: true } })
              if (linkedProducts?.length) products = (products || [])?.concat(linkedProducts)
            }
          }

        } catch (e) {
          console.warn('searchProps failed to search for matching product-components')
        }

        return { items: products, sys }
      },
      searchIdentifier: async (id: string | number) => {
        if (!id) throw Error('Missing id to query')
        if (typeof id !== 'string' && typeof id !== 'number') throw Error('Invalid id')
        const config = { limit: 10, query: { any: true }, resolve: 1 }
        const query: Array<{ select?: Array<string>, field?: string, method?: string, match?: any }> = [
          { field: 'sku', method: 'is', match: String(id).toLowerCase() },
          { field: 'ean', method: 'in', match: String(id).toLowerCase() }
        ]
        const { items: products } = await this.get('product', query.concat({ select: ['title', 'sku', 'ean', 'images', 'components'] }), config)
        if (products?.length) return products

        const { items: stock } = await this.get('stock', query.concat({ select: ['title', 'sku', 'ean', 'images'] }), config)

        if (stock?.length) {
          const { items: linkedProducts } = await this.get('product', [{ select: ['title', 'sku', 'ean', 'images', 'components'] }, ...stock.map(s => ({ field: 'components', method: 'in', match: s.sys.id, type: 'uuid' }))], config)
          return linkedProducts
        }

      },
      deleteCascade: async (
        productId,
        linksToDelete = { catalogue_products: false, stock_items: false, variants: false }
      ) => {
        return await this.delete('product', productId, { cascade: linksToDelete })
      }
    }
  }

  get inventory () {
    return {
      // NOTE these  update the inventory-location as wel as the stock-ledger count
      // This should mostly be the prefered way of updating anything stock / inventory -count related from the UI
      add: async (stock_item: UUID, count, catalogue?: UUID) => {
        try {
          const updated = await this.put('inventory.add', location, { location, stock_item, count, catalogue })
          if (updated) return await this.put('stock.add', stock_item, { stock_item, count, catalogue })
        } catch (e) {
          void this.dispatch('Error', e)
        }
      },
      subtract: async (stock_item: UUID, count, catalogue?: UUID) => {
        try {
          const updated = await this.put('inventory.subtract', location, { location, stock_item, count, catalogue })
          if (updated) return await this.put('stock.subtract', stock_item, { stock_item, count, catalogue })
        } catch (e) {
          void this.dispatch('Error', e)
        }
      },
      transit: {
        start: async (id, count) => {
          return this.put('inventory.transit.start', id, { count })
        },
        resolve: async (id, destination, count) => {
          return this.put('inventory.transit.resolve', id, { destination, count })
        },
        cancel: async (id, count, destination) => {
          return this.put('inventory.transit.cancel', id, { count, destination })
        }
      },

      // @CtInventoryLocation
      // NOTE these directly update the inventory-location count, without touching the stock-ledger. Prefer the genral inventory.add/subtract methods above
      location: {
        add: async (location: UUID, stock_item: UUID, count, catalogue?: UUID) => {
          // console.log('location.add', location, stock_item, count, catalogue)
          return await this.put('inventory.add', location, { location, stock_item, count, catalogue }).catch(e => this.handlers.Error(e))
        },
        subtract: async (location: UUID, stock_item: UUID, count, catalogue?: UUID) => {
          // console.log('location.subtract', location, stock_item, count, catalogue)
          // COMBAK this should (have the option to) also update the stock when inventory is directly updated
          return await this.put('inventory.subtract', location, { location, stock_item, count, catalogue }).catch(e => this.handlers.Error(e))
        }
      },

      storage_location: {
        update: (location, buffer) => {
          return this.put('storage_location', [{ field: 'sys.id', method: 'match', match: location }], { buffer: buffer })
        }
      },

      // @CtStockLedger
      // NOTE these directly update the stock-ledger count, without touching the inventory-location. Prefer the genral inventory.add/subtract methods above
      stock: {
        add: async (stock_item: UUID, count, catalogue?: UUID) => {
          // console.log('stock.add', stock_item, count, catalogue)
          return await this.put('stock.add', stock_item, { stock_item, count, catalogue }).catch(e => this.handlers.Error(e))
        },
        subtract: async (stock_item: UUID, count, catalogue?: UUID) => {
          // console.log('stock.subtract', stock_item, count, catalogue)
          return await this.put('stock.subtract', stock_item, { stock_item, count, catalogue }).catch(e => this.handlers.Error(e))
        }
      }
    }
  }

  // @CtInvoice
  get invoice () {
    return {
      get: async (query = []) => {
        return await this.get('invoice', query).catch(e => this.handlers.Error(e))
      },
      generate: async (generationData: InvoiceGenerationDef, config?) => {
        return await this.put('invoice.generate', [], generationData, config).catch(e => this.handlers.Error(e))
      },
      save: async (payload: InvoiceDefinition, config?) => {
        return await this.put('invoice.save', payload.id, payload).catch(e => this.handlers.Error(e))
      },
      reset: async (id, config?) => {
        return await this.put('invoice.reset', id, undefined, config).catch(e => this.handlers.Error(e))
      },
      markPaid: async (id) => {
        return await this.put('invoice.paid', id, undefined).catch(e => this.handlers.Error(e))
      }
    }
  }

  get draft () {
    return {
      delete: async (id: UUID) => this.delete('draft', id as any)
    }
  }

  // @CtSourceOrder, CtSupplier, CtSupplierCatalogue
  get source () {
    return {
      order: {
        draft: async (data: Partial<CtSourceOrder>) => {
          return await this.put('source.order.draft', data.id, data).catch(e => this.handlers.Error(e))
        },
        create: async (data: CtSourceOrder) => {
          return await this.put('source.order.create', data.id, data).catch(e => this.handlers.Error(e))
        },
        update: async (id: UUID, data: Partial<CtSourceOrder>) => {
          return await this.patch('source.order.update', id, data).catch(e => this.handlers.Error(e))
        },
        cancel: async (id: UUID) => {
          // FIXME: fix typing for RequestQueryItem since it does not take a string or string-array
          return await this.delete('source.order.cancel', [{ field: '__id', match: id }]).catch(e => this.handlers.Error(e))
        },
        resolve: async (id: UUID, data) => {
          return await this.patch('source.order.resolve', id, data).catch(e => this.handlers.Error(e))
        }
      }
    }
  }

  // @CtTask
  get tasks () {
    return {
      list: async (query = []) => {
        return await this.get('task', query).catch(e => this.handlers.Error(e))
      },
      get: async (id: UUID) => {
        return await this.get('task', id).catch(e => this.handlers.Error(e))
      },
      create: async (data: CtTask) => {
        return await this.put('task', data.id, data).catch(e => this.handlers.Error(e))
      },
      update: async (id: UUID, data: Partial<CtTask>) => {
        return await this.patch('task', id, data).catch(e => this.handlers.Error(e))
      },
      started: async (id: UUID) => {
        return await this.patch('task', id, { status: [Status.Started] }).catch(e => this.handlers.Error(e))
      },
      selection: async (id: UUID, selection: SelectionDict) => {
        return await this.patch('task', id, { status: [Status.Active], selection }).catch(e => this.handlers.Error(e))
      },
      complete: async (id: UUID) => {
        return await this.patch('task', id, { status: [Status.Final] }).catch(e => this.handlers.Error(e))
      },
      delete: async (id: UUID) => {
        return await this.delete('task', id as any).catch(e => this.handlers.Error(e))
      }
    }
  }

  // @CtSource
  // get sources () {
  //   return {
  //     update: async (id: UUID, selection: Array<string>) => {
  //       return await this.put('source.update', id, selection).catch(e => this.handlers.Error(e))
  //     },
  //     archive: async (id: UUID, selection: Array<string>) => {
  //       return await this.put('source.archive', id, selection).catch(e => this.handlers.Error(e))
  //     }
  //   }
  // }

  get insight () {
    return {
      get: async (type: 'order' | 'product', timerange: { start: Date | string, end: Date | string }, resolution = 'day'): Promise<ServiceResponse<InsightPoint>> => {
        // console.log(`insights.${type}`, [{field: 'start', match: timerange.start}, {field: 'end', match: timerange.end}, {field: 'resolution', match: resolution}])
        return this.get(`insights.${type}`, timerangeQuery(timerange, resolution))
      }
    }
  }

  // @CtImport
  get import () {
    return {
      info: async (data) => {
        return await this.get('import.info', data).catch(e => this.handlers.Error(e))
      },
      generate: async (name, data) => {
        return await this.post('import.generate', { endpoint: 'create' }, { name, data }).catch(e => this.handlers.Error(e))
      }
    }
  }

  get notifications () {
    return {
      count: async (query) => {
        return this.get('notifications.count', query)
      },
      list: async (query, config) => {
        return this.get('notifications', query, config)
      },
      get: async (id) => {
        return this.get('notification', id)
      },
      clear: async () => {
        return this.delete('notifications.clear')
      },
      remove: async (id) => {
        return this.delete('notification', id)
      }
    }
  }

  static get StatusPattern (): StatusPatternObj {
    return {
      // return statuses that can be reached from the current status array
      options (current: Array<Status>) {
        return Object.values(Directr.StatusPattern).reduce((options, value) => {
          if (typeof value === 'function' || value.from === false) return options
          else if (value.from === true || current.every(c => (value.from as Array<Status>)?.includes(c))) options.push(value)
          return options
        }, [] as Array<StatusPattern>)
      },
      compare (method: 'gt' | 'gte' | 'lt' | 'lte', lhs: Array<Status>, rhs: Status) {
        if (!lhs?.length) return false
        const comparator = Directr.StatusPattern[rhs]
        if (!comparator) throw Error(`StatusPattern.compare received invalid comparator "${rhs}"`)

        switch (method) {
          case 'gt': return lhs.some(s => (Directr.StatusPattern[s]?.index || 0) > (comparator.index || 0))
          case 'gte': return lhs.some(s => (Directr.StatusPattern[s]?.index || 0) >= (comparator.index || 0))
          case 'lt': return lhs.some(s => (Directr.StatusPattern[s]?.index || 0) < (comparator.index || 0))
          case 'lte': return lhs.some(s => (Directr.StatusPattern[s]?.index || 0) <= (comparator.index || 0))
          default:
            throw Error(`StatusPattern.compare received invalid compare method "${method}"`)
        }
      },
      parse (from: Array<Status>, to: Array<Status>, clean?: boolean) {
        if (!from?.filter(Boolean).length) return to
        if (!to?.filter(Boolean).length) return from

        const rejected: Array<{ from: string, to?: string, reason: string }> = []
        const fromPatterns = Object.fromEntries(from.filter(Boolean).map(f => [f, this[f]]))
        const toPatterns = Object.fromEntries(to.filter(Boolean).map(f => [f, this[f]]))

        const valid = Object.entries(fromPatterns).every(([status, f]) => {
          // if the from-status is additive, return true regardless (if also immutable, it will be merged later)
          if (f.additive === true) return true
          // check if the from-status is immutable or does not allow any to-targets
          if (f.immutable === true || f.to === false) {
            rejected.push({ from: status, reason: 'source status is immutable' })
            return false
          }
          // if all targets are allowed, return
          if (f.to === true) return true

          const toConfig = f.to

          // check if the from-status config allows the target statuses (and vice-versa)
          return to.every(t => {
            const valid = (status === t) || toConfig?.includes(t) || toPatterns[t].additive
            if (!valid) rejected.push({ from: status, to: t, reason: 'source status does not accept change to target status' })
            return valid
          })
            && Object.entries(toPatterns).every(
              ([_s, t]) => {
                const valid = !!t?.from || t?.additive || (typeof t.from !== 'boolean' && t.from?.includes(status as Status))
                if (!valid) rejected.push({ from: status, to: String(t?.to), reason: 'target status does not accept change from source status' })
                return valid
              })
        })

        if (!valid) throw Error(`Could not update status-array from the current "${from}" to "${to}". Not all constituents of the current status-array are allowed to be changes to the new status array: ${rejected.map(r => `
        "${r.from}"${r.to ? `-> "${r.to}"` : ''} : ${r.reason}`)}`)

        // store the highest encountered weight
        let weight = 0
        return from.filter(Boolean).concat(to.filter(Boolean)).reduce<Array<Status>>((current, status) => {
          if (current.includes(status)) return current
          const pattern: StatusPattern = this[status]
          const overwrite = ((pattern.weight || 0) >= weight) && !pattern.additive
          if (overwrite) weight = (pattern.weight || 0)

          // if overwrite is true, overwrite all non-additive statuses from the current result
          current = current.concat(status)
            .filter(s => {
              // if (overwrite && s !== status && !(fromPatterns[s] || toPatterns[s])?.additive) return false
              const additive = (fromPatterns[s] || toPatterns[s])?.additive
              return clean
                ? overwrite ? !(s !== status && !toPatterns[s]?.additive) : !!toPatterns[s]
                : (fromPatterns[s] && !toPatterns[s])
                  ? !overwrite || fromPatterns[s].additive || !current.length // if "old" status, it needs to be additive to stay, or overwrite needs to be false
                  : overwrite ? (s === status || additive) : (current.includes(s) || additive) // if "new" status, it needs to be heavier
            })

          return current.sort((n, _s) => this[n].additive ? 1 : -1)
        }, [])
      },
      [Status.Draft]: { id: Status.Draft, index: 0, additive: false, immutable: false, from: false, to: [Status.New] },
      [Status.New]: {
        id: Status.New,
        index: 1,
        additive: false,
        immutable: false,
        from: [Status.Draft],
        to: true // to give freedom when the order has not yet been interacted with
      },
      [Status.Pending]: {
        id: Status.Pending, index: 2, additive: false, immutable: false, from: [
          Status.Queued,
          Status.New,
          Status.Cancelled
        ],
        to: true, // to give freedom when the order has not yet been interacted with
        weight: 1
      },
      [Status.Queued]: {
        id: Status.Queued, index: 3, additive: false, immutable: false, from: [
          Status.Pending,
          Status.Starting,
          Status.Started,
          Status.Resolved
        ],
        to: true, // to give freedom when the order has not yet been interacted with
        weight: 1
      },
      [Status.Starting]: { id: Status.Starting, index: 4, weight: 1, additive: false, immutable: false, from: [Status.Queued], to: [Status.Started, Status.Queued] },
      [Status.Started]: {
        id: Status.Started, index: 5, weight: 2, additive: false, immutable: false, from: [Status.Starting], to: [
          Status.Active,
          Status.Queued, // conditional
          Status.Resolved
        ]
      },
      [Status.Active]: { id: Status.Active, index: 6, weight: 2, additive: false, immutable: false, from: [Status.Started], to: [Status.Resolved, Status.Started] },
      [Status.Resolved]: {
        id: Status.Resolved, index: 7, weight: 3, additive: false, immutable: false, from: [Status.Active, Status.Started], to: [
          Status.Final,
          Status.Replaced,
          Status.Queued, // conditional
          Status.Archived,
          Status.Deleted,
          Status.Transiting
          // Status.Cancelled // conditional
        ]
      },
      [Status.Transiting]: { id: Status.Transiting, index: 8, additive: false, immutable: false, from: true, to: true },
      [Status.Ready]: { id: Status.Ready, index: 8, additive: false, immutable: false, from: true, to: true },
      // TODO: support a "being returned"/"returned" status
      [Status.Final]: { id: Status.Final, index: 9, weight: 4, additive: false, immutable: false, from: [Status.Transiting, Status.Resolved], to: [Status.Replaced, Status.Archived, Status.Deleted] },
      [Status.Cancelled]: {
        id: Status.Cancelled, weight: 9, additive: false, immutable: false, from: [
          Status.New,
          Status.Pending,
          Status.Queued,
          Status.Starting,
          Status.Active
          // Status.Resolved // conditional
        ],
        to: [
          Status.Pending, // conditional
          Status.Queued, // conditional
          Status.Archived,
          Status.Deleted
        ]
      },
      [Status.Deleted]: { id: Status.Deleted, weight: 10, additive: false, immutable: true, from: true },
      [Status.Archived]: { id: Status.Archived, weight: 10, additive: false, immutable: true, from: true },
      [Status.Replaced]: { id: Status.Replaced, weight: 9, additive: false, immutable: true, from: [Status.Resolved, Status.Final] },

      [Status.Stopped]: { id: Status.Stopped, weight: 8, additive: false, immutable: true },

      [Status.Updated]: { id: Status.Updated, additive: true, immutable: false },
      [Status.Failed]: { id: Status.Failed, additive: true, immutable: false },
      [Status.Paused]: { id: Status.Paused, additive: true, immutable: false },

      [Status.Unsourced]: { id: Status.Unsourced, additive: false, immutable: false, from: true, to: [Status.Sourced, Status.Final] },
      [Status.Sourcing]: { id: Status.Sourcing, additive: false, immutable: false, from: true, to: [Status.Unsourced, Status.Sourced, Status.Final] },
      [Status.Sourced]: { id: Status.Sourced, additive: false, immutable: false, from: [Status.Unsourced, Status.Sourcing], to: [Status.Final] }
    } as any
  }
}
